import React, { useEffect, useRef } from 'react';

import {
  Box,
  Button,
  CircularProgress,
  Hidden,
  TextField,
} from '@mui/material';

import Autocomplete from '@mui/material/Autocomplete';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SearchIcon from '@mui/icons-material/Search';

import CarCard from './CarCard';

import axiosInstance from '../../lib/axiosInstance';
import gaEvent from '../../lib/gaEvent';


const ChooseCar = ({
  handleNextStep,
  setActiveStep,
  car,
  setCar,
  carSearchData,
  setCarSearchData,
  carReady,
  setCarReady,
  getEstimate,
}) => {

  const submitCar = () => {
    gaEvent({
      category: 'Request Quotes',
      action: 'Submit Car',
      label: 'Select Car',
    });

    if (localStorage.getItem('personalQuote')) {
      handleNextStep(2);
      localStorage.removeItem('personalQuote');
    } else {
      localStorage.removeItem('hooked')
      handleNextStep(1);
      getEstimate();
    }
  }

  const modelInputRef = useRef();
  const yearInputRef = useRef();
  const typeInputRef = useRef();

  const { make, model, year, type } = car;
  const { makes, models, years, types } = carSearchData;

  const setMake = value => {
    setCarReady(false);
    setCar({ make: value, model: '', year: '', type: '', vin: car.vin });
    setCarSearchData({ makes, models: [], years: [], types: [] });

    (async () => {
      try {
        const result = await axiosInstance(`/api/v2/car-models/?make=${value}`);
        setCarSearchData({ ...carSearchData, models: result.data });
        modelInputRef.current.focus();
      } catch (error) {
        console.log('models error', error);
      }
    })();
  };

  const setModel = value => {
    setCarReady(false);
    setCar({ make, model: value, year: '', type: '', vin: car.vin });
    setCarSearchData({ makes, models, years: [], types: [] });

    (async () => {
      try {
        const result = await axiosInstance(`api/v2/car-years/?make=${make}&model=${value}`);
        setCarSearchData({ ...carSearchData, years: ["I'm not sure", ...result.data] });
        yearInputRef.current.focus();
      } catch (error) {
        console.log('years error', error);
      }
    })();
  };

  const setYear = value => {
    if (value === "I'm not sure") {
      setCar({ make, model, year: '', type: '', vin: car.vin });
      setCarReady(true);
      return;
    }

    setCarReady(false);
    setCar({ make, model, year: value, type: '', vin: car.vin });
    setCarSearchData({ makes, models, years, types: [] });

    (async () => {
      try {
        const result = await axiosInstance(`/api/v2/car-types/?make=${make}&model=${model}&year=${value}`);
        setCarSearchData({ ...carSearchData, types: ["I'm not sure", ...result.data] });
        typeInputRef.current.focus();
      } catch (error) {
        console.log('types error', error);
      }
    })();
  };

  const setType = value => {
    typeInputRef.current.blur();
    setCar({
      make,
      model,
      year,
      type: value === "I'm not sure" ? '' : value,
      vin: car.vin
    });
    setCarReady(true);
  };

  useEffect(() => {
    if (make && !model) {
      setMake(make);
    } else if (model && !year) {
      setModel(model);
    }
  }, [])

  return <>
    {!make && (
      <>
        <Hidden mdDown>
          <Box width={800} pb={64}>
            <Autocomplete
              open={true}
              options={makes}
              value={make}
              onChange={(event, value) => {
                setMake(value);
              }}
              autoComplete={true}
              autoHighlight={true}
              openOnFocus={true}
              sx={{
                backgroundColor: '#fff',
                borderRadius: 2,
                '& .MuiAutocomplete-popupIndicatorOpen': {
                  transform: 'none',
                },
              }}
              popupIcon={<SearchIcon />}
              forcePopupIcon
              renderOption={(props, option) => (
                <Box display="flex" alignItems="center" {...props}>
                  {option}
                </Box>
              )}
              renderInput={params => (
                <TextField
                  {...params}
                  placeholder="Search by make"
                  variant="outlined"
                  fullWidth
                />
              )}
            />
          </Box>
        </Hidden>
        <Hidden mdUp>
          <Box width="100%">
            <Autocomplete
              options={makes}
              value={make}
              onChange={(event, value) => {
                setMake(value);
              }}
              autoComplete={true}
              autoHighlight={true}
              openOnFocus={true}
              open={true}
              popupIcon={<SearchIcon />}
              forcePopupIcon
              renderOption={(props, option) => (
                <Box display="flex" alignItems="center" {...props}>
                  {option}
                </Box>
              )}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Make"
                  placeholder="Type to search"
                  autoFocus={true}
                  variant="outlined"
                  fullWidth
                />
              )}
            />
          </Box>
        </Hidden>
      </>
    )}
    {make && (
      <>
        <Box width={{ xs: '100%', md: 900 }} display="flex">
          <Hidden mdDown>
            <CarCard car={car} setCar={setCar} setActiveStep={setActiveStep} />
          </Hidden>
          <Box
            flex={1}
            ml={{ xs: 0, md: 4 }}
            bgcolor={{ xs: 'unset', md: '#fff' }}
            px={{ xs: 0, md: 2 }}
            py={{ xs: 0, md: 1 }}
            borderRadius="undefinedpx"
          >
            <Autocomplete
              options={makes}
              value={make}
              onChange={(event, value) => {
                if (value) {
                  setMake(value);
                }
              }}
              autoComplete={true}
              autoHighlight={true}
              openOnFocus={true}
              getOptionLabel={option => option.toString()}
              renderOption={(props, option) => (
                <Box display="flex" alignItems="center" {...props}>
                  {option.toString()}
                </Box>
              )}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Car Make"
                  placeholder="Choose your make"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                />
              )}
            />
            <Autocomplete
              options={models || []}
              value={model}
              onChange={(event, value) => {
                if (value) {
                  setModel(value);
                }
              }}
              autoComplete={true}
              autoHighlight={true}
              openOnFocus={true}
              disabled={models?.length === 0}
              popupIcon={
                models?.length > 0 ? (
                  <ArrowDropDownIcon />
                ) : (
                  <CircularProgress color="primary" size={24} />
                )
              }
              renderInput={params => (
                <TextField
                  {...params}
                  label="Car Model"
                  placeholder="Choose your model"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  inputRef={modelInputRef}
                />
              )}
            />
            {model && (
              <Autocomplete
                options={years || []}
                value={year}
                onChange={(event, value) => {
                  if (value) {
                    setYear(value);
                  }
                }}
                autoComplete={true}
                autoHighlight={true}
                openOnFocus={true}
                getOptionLabel={option => option.toString()}
                disabled={years?.length === 0}
                popupIcon={
                  years?.length > 0 ? (
                    <ArrowDropDownIcon />
                  ) : (
                    <CircularProgress color="primary" size={24} />
                  )
                }
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Car Year"
                    placeholder="Choose your year"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    inputRef={yearInputRef}
                  />
                )}
              />
            )}
            {year && year !== "I'm not sure" && (
              <Autocomplete
                options={types || []}
                value={type}
                onChange={(event, value) => {
                  if (value) {
                    setType(value);
                  }
                }}
                autoComplete={true}
                autoHighlight={true}
                openOnFocus={true}
                disabled={types.length === 0}
                popupIcon={
                  types.length > 0 ? (
                    <ArrowDropDownIcon />
                  ) : (
                    <CircularProgress color="primary" size={24} />
                  )
                }
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Car Type"
                    placeholder="Choose your type"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    inputRef={typeInputRef}
                  />
                )}
              />
            )}
            {carReady && (
              <Box mt={2} textAlign={{ xs: 'center', md: 'unset' }}>
                <Button
                  variant="contained"
                  color="secondary"
                  size="large"
                  onClick={() => {
                    setCar({ make, model, year, type, vin: car.vin });
                    submitCar();
                  }}
                >
                  Continue
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </>
    )}
  </>;
};

export default ChooseCar;
